import 'hammerjs';

import { useTheme } from '@emotion/react';
import {
  Chart,
  ChartArea,
  ChartAxisDefaults,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartPane,
  ChartPanes,
  ChartSeries,
  ChartSeriesItem,
  ChartValueAxis,
  ChartValueAxisItem,
} from '@progress/kendo-react-charts';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { getSelectedFundsManagerTitle } from '!views/Market/Funds/redux';
import { getSelectedTitle } from '!views/Market/StockExchange/marketStockExchange.seletors';
import { TrlFilters } from '»components/atoms';
import { uuidSelector } from '»components/Auth';
import { getActiveMenuItemSelector } from '»components/Menus';
import { useHandleSagaError } from '»utils/customHooks';
import { TrlFormattedMessagePlain } from '»utils/i18n';

import {
  BoxStyled,
  InvisibleOverlayContainer,
  Wrapper,
} from './TrlChart.styles';
import { trlChartsServices } from './trlCharts.services';

export const TrlChart = ({ children, isFunds }) => {
  const theme = useTheme();
  const intl = useIntl();

  const [timeInterval, setTimeInterval] = useState('1m');

  const [displayChart, setDisplayChart] = useState(false);

  const [closeData, setCloseData] = useState([]);

  const [timeData, setTimeData] = useState([]);

  const selectedFund = useSelector(getSelectedFundsManagerTitle);
  const selectedTitleStockMarket = useSelector(getSelectedTitle);

  const selectedTitle = isFunds ? selectedFund : selectedTitleStockMarket;
  const uuid = useSelector(uuidSelector);
  const activeMenuItem = useSelector(getActiveMenuItemSelector);

  const { handleSagaError } = useHandleSagaError();

  const isShownOnDashboard = activeMenuItem === 'DASHBOARD';

  const handleChange = (_, newTime) => {
    setDisplayChart(false);
    setCloseData([]);
    setTimeData([]);
    setTimeInterval(newTime);
  };

  const callService = (symbol, exchange, securityType, period) => {
    trlChartsServices
      .instrumentGraphQuotes({ symbol, exchange, securityType, period })
      .then((resp) => {
        if (resp.length > 0) {
          if (resp[0].type === 'resp') resp.shift();

          setCloseData(
            resp.map((el) => {
              return el.entryPx;
            }),
          );

          setTimeData(
            resp.map(
              (el) => new Date(period === 'i' ? el.entryTime : el.entryDate),
            ),
          );
        }
      })
      .catch((error) => {
        handleSagaError(error);
      });
  };

  const selectedTitleExists = Object.keys(selectedTitle).length > 0;

  useEffect(() => {
    if (selectedTitleExists)
      callService(
        selectedTitle.symbol,
        selectedTitle.exchange || selectedTitle.securityExchange,
        selectedTitle.securityType,
        timeInterval,
      );

    return () => {
      setCloseData([]);
      setTimeData([]);
      setDisplayChart(false);
    };
  }, [
    selectedTitle.symbol,
    selectedTitle.securityType,
    timeInterval,
    selectedTitleExists,
    uuid,
    selectedTitle.exchange,
    selectedTitle.securityExchange,
  ]);

  useEffect(() => {
    if (closeData.length > 1 && timeData.length > 1) {
      setDisplayChart(true);
    } else {
      setDisplayChart(false);
    }
  }, [closeData.length, timeData.length]);

  const valueAxis = [
    {
      name: 'cota',
    },
  ];

  const filters = [
    !isFunds && {
      value: 'i',
      label: TrlFormattedMessagePlain({ id: 'TRLT_UI-INTRADAY' }),
    },
    { value: '5d', label: TrlFormattedMessagePlain({ id: 'TRLT_UI-5D' }) },
    { value: '1m', label: TrlFormattedMessagePlain({ id: 'TRLT_UI-1M' }) },
    { value: '3m', label: TrlFormattedMessagePlain({ id: 'TRLT_UI-3M' }) },
    { value: '6m', label: TrlFormattedMessagePlain({ id: 'TRLT_UI-6M' }) },
    { value: '1y', label: TrlFormattedMessagePlain({ id: 'TRLT_UI-1Y' }) },
    { value: '5y', label: TrlFormattedMessagePlain({ id: 'TRLT_UI-5Y' }) },
  ].filter(Boolean);

  const getCategoryAxisBaseUnit = () => {
    if (timeInterval === 'i') return 'minutes';

    if (timeInterval === '5d') return 'days';

    if (timeInterval === '1m' || timeInterval === '3m' || timeInterval === '6m')
      return 'weeks';

    return 'months';
  };

  return (
    <Wrapper hasHeader={!!children} isDashboard={isShownOnDashboard}>
      {children}
      <TrlFilters
        selectedFilter={timeInterval}
        filters={filters}
        handleChangeFilter={handleChange}
      />
      <BoxStyled isVisible={displayChart}>
        <Chart
          pannable={{
            lock: ['y', 'x'],
          }}
          zoomable={{
            mousewheel: {
              lock: ['y', 'x'],
            },
          }}
          style={{
            maxHeight: '230px',
            marginTop: 30,
            color: theme.palette.colors.onPrimaryBackground,
          }}
        >
          <ChartAxisDefaults />
          <ChartValueAxis>
            {valueAxis.map((item) => (
              <ChartValueAxisItem
                key={item.name}
                name={item.name}
                labels={{
                  position: 'end',
                  step: 2,
                  color: theme.palette.colors.onPrimaryBackground,
                }}
                majorGridLines={{
                  step: 2,
                  color: theme.palette.colors.lower,
                }}
                color="transparent"
              />
            ))}
          </ChartValueAxis>
          <ChartCategoryAxis>
            <ChartCategoryAxisItem
              categories={timeData}
              maxDivisions={7}
              majorGridLines={{ visible: false }}
              baseUnit={getCategoryAxisBaseUnit()}
              color={theme.palette.colors.onPrimaryBackground}
              labels={{
                content: (e) => {
                  const date = new Date(e.value);

                  if (getCategoryAxisBaseUnit() === 'minutes')
                    return date.toLocaleTimeString(intl.locale, {
                      hour: '2-digit',
                      minute: '2-digit',
                    });

                  if (getCategoryAxisBaseUnit() === 'months')
                    return date.toLocaleDateString(intl.locale, {
                      month: 'short',
                      year: '2-digit',
                    });

                  return date.toLocaleDateString(intl.locale, {
                    day: '2-digit',
                    month: '2-digit',
                  });
                },
              }}
            />
          </ChartCategoryAxis>
          <ChartPanes>
            <ChartPane height="90%" />
          </ChartPanes>
          <ChartArea
            background={
              isShownOnDashboard
                ? theme.palette.colors.secondaryBackground
                : theme.palette.colors.primaryBackground
            }
          />
          <ChartSeries>
            <ChartSeriesItem
              type="area"
              data={closeData}
              color={theme.palette.colors.graphBackground}
              line={{
                width: 2,
                color: theme.palette.colors.mainFocused,
                style: 'smooth',
              }}
              missingValues="interpolate"
              markers={{ visible: false }}
              highlight={{ visible: false }}
            />
          </ChartSeries>
        </Chart>
        <InvisibleOverlayContainer />
      </BoxStyled>
    </Wrapper>
  );
};

TrlChart.propTypes = {
  children: PropTypes.element,
  isFunds: PropTypes.bool,
};
